/**
 * @fileoverview Context Menu & PWA Install Manager
 * @module contextMenu
 * @description
 * Enterprise-grade implementation of the viewport context menu and PWA installation routines.
 * Features smart edge-collision detection, a11y keyboard navigation, advanced haptics, 
 * and defensive DOM interactions.
 */

import { log } from '../utils/logger.js';

// [FIX] AbortController для очистки глобальных слушателей
let _ac = new AbortController();

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================
const CONFIG = {
    MENU_ID: 'vp-ctx-menu',
    VIEWPORT_ID: 'viewport-container',
    LONG_TAP_DURATION: 500, // ms
    MARGIN: 8, // Edge margin in px
    VIBRATE_PATTERN: [15, 30, 15], // Advanced haptic feedback
    CSS_CLASSES: {
        VISIBLE: 'visible',
        ACTIVE: 'active',
        DISABLED: 'disabled',
        OPEN: 'open'
    }
};

// ============================================================================
// CONTEXT MENU MANAGER
// ============================================================================
class ContextMenuManager {
    constructor() {
        this.ctx = document.getElementById(CONFIG.MENU_ID);
        this.vc = document.getElementById(CONFIG.VIEWPORT_ID);
        
        this._longTapTimer = null;
        this._longTapFired = false;
        this._focusedItemIndex = -1;

        if (!this.ctx || !this.vc) {
            log.warn('[ContextMenuManager] Required DOM elements not found.');
            return;
        }

        this._bindMethods();
        this._initEvents();
    }

    _bindMethods() {
        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
        this._handleContextMenu = this._handleContextMenu.bind(this);
        this._handleTouchStart = this._handleTouchStart.bind(this);
        this._handleTouchMove = this._handleTouchMove.bind(this);
        this._handleTouchEnd = this._handleTouchEnd.bind(this);
        this._handleOutsideClick = this._handleOutsideClick.bind(this);
        this._handleMenuClick = this._handleMenuClick.bind(this);
        this._handleKeyboard = this._handleKeyboard.bind(this);
    }

    _initEvents() {
        // Desktop
        this.vc.addEventListener('contextmenu', this._handleContextMenu, { signal: _ac.signal });
        
        // [10.19.28] СЕНСОРНОГО ПУТИ ЗДЕСЬ БОЛЬШЕ НЕТ — на телефоне при долгом
        // нажатии вылезали ДВА меню сразу.
        //
        // Механизмов было два, и глушилка их не связывала. Здесь стоял
        // СОБСТВЕННЫЙ таймер на 500 мс (touchstart → setTimeout → show()), а в
        // advancedUI.js круговое меню открывается по событию contextmenu и там
        // же гасит соседей через stopImmediatePropagation. Таймер живёт отдельно
        // от события, поэтому глушилка до него не доставала: сперва по таймеру
        // открывалось это меню, следом Android присылал синтетический
        // contextmenu — и поверх ложился круг. На мыши беда не видна: таймера
        // там нет вовсе.
        //
        // По решению владельца на телефоне остаётся ОДИН круг. Долгое нажатие
        // теперь ведёт к нему — обработчик касания заведён в advancedUI рядом с
        // самим кругом, а не здесь: расчёт на синтетический contextmenu ненадёжен,
        // Android шлёт его не всегда. Правый клик мышью работает как работал.
        void this._handleTouchStart; void this._handleTouchMove; void this._handleTouchEnd;

        // Global dismiss interactions
        document.addEventListener('mousedown', this._handleOutsideClick, { signal: _ac.signal });
        document.addEventListener('touchstart', this._handleOutsideClick, { passive: true, signal: _ac.signal });
        
        // Menu execution & A11y
        this.ctx.addEventListener('click', this._handleMenuClick, { signal: _ac.signal });
        document.addEventListener('keydown', this._handleKeyboard, { signal: _ac.signal });
    }

    /**
     * Defensively checks if current rendering engine state is Volume/MPR.
     * @returns {boolean} True if VolumeViewport is active
     */
    _isVolumeMode() {
        try {
            const engine = window.MikhmelViewer?.getRenderingEngine?.();
            const viewports = engine?.getViewports?.() || [];
            
            return viewports.some(vp => 
                vp.type === 'volumeViewport' || 
                vp.constructor?.name?.includes('Volume')
            );
        } catch (e) {
            log.debug('[ContextMenuManager] Engine state check failed:', e.message);
            return false;
        }
    }

    /**
     * Updates UI state of menu items based on current viewer context.
     */
    _syncMenuState() {
        // Sync active tool
        const activeBtn = document.querySelector('.tool-btn.active[data-tool]');
        const curTool = activeBtn?.dataset?.tool ?? '';

        this.ctx.querySelectorAll('.ctx-item[data-ctx-tool]').forEach(item => {
            item.classList.toggle(CONFIG.CSS_CLASSES.ACTIVE, item.dataset.ctxTool === curTool);
        });

        // Sync Magnify constraint
        const magnifyItem = this.ctx.querySelector('[data-ctx-tool="Magnify"]');
        if (magnifyItem) {
            const isVol = this._isVolumeMode();
            magnifyItem.classList.toggle(CONFIG.CSS_CLASSES.DISABLED, isVol);
            magnifyItem.title = isVol ? 'Лупа работает только в режиме 1×1 (Stack)' : '';
            magnifyItem.setAttribute('aria-disabled', isVol.toString());
        }
    }

    /**
     * Smart positioning using bounding rects to prevent viewport overflow.
     */
    show(x, y) {
        this._syncMenuState();
        this._focusedItemIndex = -1; // Reset keyboard focus

        // Temporarily display block to calculate dimensions safely
        this.ctx.style.display = 'block';
        this.ctx.style.visibility = 'hidden';

        const rect = this.ctx.getBoundingClientRect();
        const w = rect.width || 190;
        const h = rect.height || 400;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Collision detection & resolution
        let finalX = Math.max(CONFIG.MARGIN, Math.min(x, vw - w - CONFIG.MARGIN));
        let finalY = Math.max(CONFIG.MARGIN, Math.min(y, vh - h - CONFIG.MARGIN));

        requestAnimationFrame(() => {
            this.ctx.style.left = `${finalX}px`;
            this.ctx.style.top = `${finalY}px`;
            this.ctx.style.visibility = 'visible';
            this.ctx.style.display = ''; // Reset to stylesheet default
            this.ctx.classList.add(CONFIG.CSS_CLASSES.VISIBLE);
        });
    }

    hide() {
        if (!this.ctx.classList.contains(CONFIG.CSS_CLASSES.VISIBLE)) return;
        this.ctx.classList.remove(CONFIG.CSS_CLASSES.VISIBLE);
        this._focusedItemIndex = -1;
    }

    // --- EVENT HANDLERS ---

    _handleContextMenu(e) {
        e.preventDefault();
        this.show(e.clientX, e.clientY);
    }

    _handleTouchStart(e) {
        if (e.target.closest('button, .vp-zoom-btn, .vp-zoom-controls')) return;
        if (e.touches.length !== 1) return;

        this._longTapFired = false;
        const touch = e.touches[0];
        const tx = touch.clientX;
        const ty = touch.clientY;

        this._longTapTimer = setTimeout(() => {
            this._longTapFired = true;
            this._triggerHaptic();
            this.show(tx, ty);
        }, CONFIG.LONG_TAP_DURATION);
    }

    _handleTouchMove() {
        this._cancelLongTap();
    }

    _handleTouchEnd() {
        this._cancelLongTap();
    }

    _cancelLongTap() {
        if (this._longTapTimer) {
            clearTimeout(this._longTapTimer);
            this._longTapTimer = null;
        }
    }

    _triggerHaptic() {
        if (!navigator.vibrate) return;
        try {
            if (window._userHasInteracted) navigator.vibrate(CONFIG.VIBRATE_PATTERN);
        } catch (e) {
            log.debug('[ContextMenuManager] Haptic error:', e.message);
        }
    }

    _handleOutsideClick(e) {
        if (this.ctx.classList.contains(CONFIG.CSS_CLASSES.VISIBLE) && !this.ctx.contains(e.target)) {
            this.hide();
        }
    }

    _handleMenuClick(e) {
        const item = e.target.closest('.ctx-item');
        if (!item || item.classList.contains(CONFIG.CSS_CLASSES.DISABLED)) return;

        this._executeAction(item);
        this.hide();
    }

    /**
     * Enterprise A11y: Keyboard Navigation for Context Menu
     */
    _handleKeyboard(e) {
        if (!this.ctx.classList.contains(CONFIG.CSS_CLASSES.VISIBLE)) return;

        const items = Array.from(this.ctx.querySelectorAll('.ctx-item:not(.disabled)'));
        if (!items.length) return;

        switch (e.key) {
            case 'Escape':
                this.hide();
                e.stopPropagation();
                break;
            case 'ArrowDown':
                e.preventDefault();
                this._focusedItemIndex = (this._focusedItemIndex + 1) % items.length;
                items[this._focusedItemIndex].focus();
                break;
            case 'ArrowUp':
                e.preventDefault();
                this._focusedItemIndex = this._focusedItemIndex <= 0 ? items.length - 1 : this._focusedItemIndex - 1;
                items[this._focusedItemIndex].focus();
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (this._focusedItemIndex >= 0) {
                    this._executeAction(items[this._focusedItemIndex]);
                    this.hide();
                }
                break;
        }
    }

    /**
     * Command Dispatcher. Proxies to existing UI managers.
     */
    _executeAction(item) {
        const { ctxTool, ctxBtn, ctxAction } = item.dataset;

        if (ctxTool) {
            if (ctxTool === 'Magnify' && this._isVolumeMode()) {
                window.showToast?.('🔎 Лупа работает только в режиме 1×1 (не MPR/Volume)', 'info', 2500);
                return;
            }
            document.querySelector(`.tool-btn[data-tool="${ctxTool}"]`)?.click();
            return;
        }

        if (ctxBtn) {
            document.getElementById(ctxBtn)?.click();
            return;
        }

        if (ctxAction === 'fullscreen') {
            this._toggleFullscreen();
        } else if (ctxAction === 'copy-hu') {
            this._copyHUToClipboard();
        } else if (ctxAction === 'screenshot') {
            this._quickScreenshot();
        } else if (ctxAction?.startsWith('preset-')) {
            this._applyQuickPreset(ctxAction.replace('preset-', ''));
        }
    }

    _toggleFullscreen() {
        const doc = window.document;
        const el = doc.documentElement;

        const requestFS = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
        const exitFS = doc.exitFullscreen || doc.webkitExitFullscreen || doc.mozCancelFullScreen || doc.msExitFullscreen;

        if (!doc.fullscreenElement && !doc.webkitFullscreenElement) {
            requestFS?.call(el);
        } else {
            exitFS?.call(doc);
        }
    }

    /**
     * Copy current HU value under cursor to clipboard.
     */
    _copyHUToClipboard() {
        const huEl = document.getElementById('hu-value-display') || document.getElementById('hu-value-display-m');
        const huText = huEl?.textContent?.trim() || 'HU: —';
        navigator.clipboard?.writeText(huText).then(() => {
            window.showToast?.(`📋 ${huText} скопировано`, 'success', 1500);
        }).catch(() => {
            window.showToast?.(huText, 'info', 2000);
        });
    }

    /**
     * Quick W/L preset from context menu.
     */
    _applyQuickPreset(preset) {
        const presets = {
            'brain': { ww: 80, wl: 40 },
            'lung': { ww: 1500, wl: -600 },
            'bone': { ww: 2500, wl: 480 },
            'abdomen': { ww: 400, wl: 50 },
            'mediastinum': { ww: 350, wl: 50 },
            'liver': { ww: 150, wl: 30 },
            'stroke': { ww: 8, wl: 32 },
            'pe': { ww: 700, wl: 100 },
        };
        const p = presets[preset];
        if (!p) return;
        try {
            const chip = document.querySelector(`.preset-chip[data-ww="${p.ww}"][data-wc="${p.wl}"]`);
            if (chip) { chip.click(); return; }
            // Direct API call
            if (window.MikhmelViewer?.setWindowLevel) {
                window.MikhmelViewer.setWindowLevel(p.ww, p.wl);
            }
            window.showToast?.(`W/L: ${p.ww}/${p.wl}`, 'info', 1200);
        } catch (e) {
            log.debug('[ContextMenu] Quick preset error:', e.message);
        }
    }

    /**
     * Take quick screenshot from context menu.
     */
    _quickScreenshot() {
        const btn = document.getElementById('btn-screenshot');
        if (btn) { btn.click(); return; }
        window.showToast?.('📸 Скриншот...', 'info', 1000);
    }
}

// ============================================================================
// PWA INSTALL MANAGER
// ============================================================================
class PWAManager {
    constructor() {
        this.overlay = document.getElementById('pwa-modal-overlay');
        this.btnInstall = document.getElementById('pwa-m-do-install');
        this.btnCancel = document.getElementById('pwa-m-cancel');
        this.featuresPanel = document.getElementById('pwa-m-features');

        if (!this.overlay) return;

        this.env = {
            isIOS: /iPhone|iPad|iPod/.test(navigator.userAgent),
            isAndroid: /Android/.test(navigator.userAgent),
            isStandalone: window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone
        };

        this._initEvents();
    }

    _initEvents() {
        this.btnCancel?.addEventListener('click', () => this.hide(), { signal: _ac.signal });
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) this.hide();
        }, { signal: _ac.signal });

        // Expose to global for old inline onclicks if they still exist
        window._mobilePWAInstall = this.promptInstall.bind(this);
    }

    show() {
        this.overlay.classList.add(CONFIG.CSS_CLASSES.OPEN);
    }

    hide() {
        this.overlay.classList.remove(CONFIG.CSS_CLASSES.OPEN);
    }

    promptInstall() {
        if (this.env.isStandalone) {
            window.showToast?.('✅ Уже работает как установленное приложение', 'success', 2000);
            return;
        }

        this._renderModalContent();
        this.show();
    }

    _renderModalContent() {
        const featuresBase = '✓ Полный оффлайн-доступ<br>✓ Данные не покидают устройство<br><br>';

        if (this.env.isIOS) {
            this._setContent(
                `${featuresBase}<b style="color:#dcdce8;">Как установить на iOS:</b><br>` +
                '1. Откройте в <b style="color:#4f8cff;">Safari</b> (не Chrome)<br>' +
                '2. Нажмите <b style="color:#4f8cff;">⬆️ Поделиться</b><br>' +
                '3. Выберите <b style="color:#4f8cff;">«На экран Домой»</b>',
                'Понятно',
                () => this.hide()
            );
        } else if (window._deferredInstallPrompt) {
            this._setContent(
                `${featuresBase}<b style="color:#dcdce8;">Нативная установка:</b><br>` +
                'Нажмите кнопку ниже для быстрой установки на ваше устройство.',
                'Установить',
                async () => {
                    const prompt = window._deferredInstallPrompt;
                    prompt.prompt();
                    const { outcome } = await prompt.userChoice;
                    
                    if (outcome === 'accepted') {
                        window.showToast?.('✅ Приложение установлено!', 'success', 3000);
                        document.getElementById('pwa-install-banner')?.classList.remove(CONFIG.CSS_CLASSES.VISIBLE);
                    }
                    
                    window._deferredInstallPrompt = null;
                    this.hide();
                }
            );
        } else {
            const tip = this.env.isAndroid
                ? '1. Нажмите <b style="color:#4f8cff;">⋮</b> (три точки)<br>2. <b style="color:#4f8cff;">«Установить приложение»</b>'
                : '1. Chrome/Edge: <b style="color:#4f8cff;">⋮ → Установить</b><br>2. Firefox: не поддерживает PWA';
            
            this._setContent(
                `${featuresBase}<b style="color:#dcdce8;">Как установить:</b><br>${tip}`,
                'Понятно',
                () => this.hide()
            );
        }
    }

    _setContent(html, btnText, action) {
        if (this.featuresPanel) this.featuresPanel.innerHTML = html;
        if (this.btnInstall) {
            this.btnInstall.textContent = btnText;
            this.btnInstall.onclick = action;
        }
    }
}

// ============================================================================
// EXPORT INITIALIZATION
// ============================================================================
export function initContextMenu() {
  // [FIX] Cleanup previous listeners
  if (_ac) _ac.abort();
  _ac = new AbortController();
    try {
        new ContextMenuManager();
        new PWAManager();
        log.info('[Mikhmel] Enterpise Context Menu & PWA Modal initialized successfully.');
    } catch (error) {
        log.error('[Mikhmel] Failed to initialize ContextMenu/PWA modules:', error);
    }
}
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Custom menu items, themes, export
// ══════════════════════════════════════════════════════════════

/** @type {Array<{label:string, icon:string, action:Function}>} */
const _customMenuItems = [];

/**
 * Add custom item to context menu
 * @param {string} label
 * @param {string} icon — emoji
 * @param {Function} action
 */
export function addContextMenuItem(label, icon, action) {
  _customMenuItems.push({ label, icon, action });
  log.debug(`[ContextMenu] Custom item added: ${label}`);
}

/**
 * Remove custom item by label
 * @param {string} label
 */
export function removeContextMenuItem(label) {
  const idx = _customMenuItems.findIndex(i => i.label === label);
  if (idx >= 0) _customMenuItems.splice(idx, 1);
}

/**
 * List all custom items
 * @returns {Array<{label:string, icon:string}>}
 */
export function listCustomMenuItems() {
  return _customMenuItems.map(i => ({ label: i.label, icon: i.icon }));
}

/**
 * Set context menu enabled/disabled
 * @param {boolean} enabled
 */
export function setContextMenuEnabled(enabled) {
  const menu = document.getElementById(CONFIG.MENU_ID);
  if (menu) menu.dataset.disabled = enabled ? '' : 'true';
}

/**
 * Get context menu state
 * @returns {Object}
 */
export function getContextMenuState() {
  const menu = document.getElementById(CONFIG.MENU_ID);
  return {
    exists: !!menu,
    visible: menu?.classList.contains(CONFIG.CSS_CLASSES.VISIBLE) || false,
    customItems: _customMenuItems.length,
    disabled: menu?.dataset.disabled === 'true',
  };
}


/** [FIX] Очистка глобальных слушателей */
export function destroyContextMenu() {
  if (_ac) { _ac.abort(); _ac = null; }
}
